import { useState } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton, {
  BUTTON_TYPES,
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { OVERLAY_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-overlay';
import BpkSwitch, { SWITCH_VARIANTS } from '@skyscanner-internal/backpack-web/bpk-component-switch';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import BpkVisuallyHidden from '@skyscanner-internal/backpack-web/bpk-component-visually-hidden';
import { translate } from '@web-platform/shared-universal-translations';
import classNames from 'classnames';

import { SponsoredHeroBanner } from '../../SponsoredHeroBanner/SponsoredHeroBanner';
import { SponsoredInfoButton } from '../../SponsoredInfoComponents/SponsoredInfoButton/SponsoredInfoButton';
import { getSponsoredCallToActionData } from '../../SponsoredInfoComponents/getSponsoredCallToActionData';
import { handleToggleEnterKey } from '../handleToggleEnterKey';

import STYLES from './ExpandedBanner.module.scss';

type ExpandedBannerProps = {
  heroImageUrl: string;
  partnerLogo: string;
  partnerName: string;
  titleText: string;
  subtitleText?: string;
  ctaButtonLabel?: string;
  isToggleOn: boolean;
  isDrawerOpen?: boolean;
  onToggleChange: () => void;
  onCtaClick?: () => void;
  pioPartnerName: string;
  pioAgencyName?: string;
};

const ExpandedBanner = ({
  ctaButtonLabel,
  heroImageUrl,
  isDrawerOpen = false,
  isToggleOn,
  onCtaClick,
  onToggleChange,
  partnerLogo,
  partnerName,
  pioAgencyName,
  pioPartnerName,
  subtitleText,
  titleText,
}: ExpandedBannerProps) => {
  const isMobile = !!useMediaQuery(BREAKPOINTS.MOBILE);
  const isTabletOrSmaller = !!useMediaQuery(BREAKPOINTS.TABLET);
  const [logoError, setLogoError] = useState(false);

  const toggleLabel = translate('ktxt_ad_partner_flights_only', { partner: partnerName });

  const onToggleKeyDown = handleToggleEnterKey(onToggleChange);

  const callToActionData = getSponsoredCallToActionData({ pioPartnerName, pioAgencyName });

  return (
    <SponsoredHeroBanner
      imageUrl={heroImageUrl}
      overlayType={OVERLAY_TYPES.solidHigh}
      mobileOverlayType={OVERLAY_TYPES.solidHigh}
      containerClassName={
        ctaButtonLabel && onCtaClick ? STYLES.bannerContainerCta : STYLES.bannerContainer
      }
    >
      <div
        className={classNames(STYLES.foregroundContent, {
          [STYLES.foregroundContentCta]: ctaButtonLabel && onCtaClick,
        })}
        data-testid="sponsored-header-expanded"
      >
        <BpkVisuallyHidden as="h2">
          {translate('ktxt_a11y_sponsored_partner_message', {
            partner: partnerName,
            message: titleText,
          })}
        </BpkVisuallyHidden>
        {/* Top section: logo + "In partnership with" badge */}
        <div className={STYLES.topSection}>
          <div className={STYLES.partnerBlock}>
            {logoError ? (
              <BpkText
                textStyle={TEXT_STYLES.label1}
                className={STYLES.logoFallback}
                aria-hidden="true"
              >
                {partnerName}
              </BpkText>
            ) : (
              <img
                src={partnerLogo}
                alt=""
                aria-hidden
                className={STYLES.logo}
                onError={() => setLogoError(true)}
              />
            )}
            <div className={STYLES.sponsoredButton}>
              <SponsoredInfoButton
                infoIconCustomColor
                callToAction={callToActionData}
                ariaLabel={`${translate('ktxt_partnership_with', {
                  partner_name: partnerName,
                })}. ${translate('ktxt_a11y_Ad_info_button')}`}
              >
                <BpkText tagName="span" className={STYLES.partnerText} aria-hidden="true">
                  {translate('homepageHero_in_partnership_with', { partner: partnerName })}
                </BpkText>
              </SponsoredInfoButton>
            </div>
          </div>
        </div>

        {/* Content group: headline + subtitle/CTA/toggle */}
        <div className={STYLES.contentGroup}>
          <div className={STYLES.mainContent}>
            <div
              className={classNames(STYLES.headline, {
                [STYLES.headlineCta]: ctaButtonLabel && onCtaClick,
              })}
            >
              <BpkText textStyle={TEXT_STYLES.heading1} aria-hidden="true">
                {titleText}
              </BpkText>
            </div>
          </div>

          {/* Bottom section: subtitle + CTA (left) | toggle (right) */}
          <div
            className={classNames(STYLES.bottomSection, {
              [STYLES.bottomSectionCta]: ctaButtonLabel && onCtaClick,
            })}
          >
            {!isMobile && (
              <BpkText
                textStyle={isTabletOrSmaller ? TEXT_STYLES.bodyDefault : TEXT_STYLES.subheading}
                className={classNames(STYLES.subtitle, {
                  [STYLES.subtitleCta]: ctaButtonLabel && onCtaClick,
                })}
                aria-hidden="true"
              >
                {subtitleText}
              </BpkText>
            )}
            <div
              className={classNames(STYLES.bottomLeft, {
                [STYLES.bottomLeftCta]: ctaButtonLabel && onCtaClick,
              })}
            >
              {ctaButtonLabel && onCtaClick && (
                <BpkButton
                  type={BUTTON_TYPES.featured}
                  size={isMobile || isTabletOrSmaller ? SIZE_TYPES.small : SIZE_TYPES.large}
                  onClick={onCtaClick}
                  className={STYLES.ctaButton}
                  aria-haspopup="dialog"
                  aria-expanded={isDrawerOpen}
                >
                  {ctaButtonLabel}
                </BpkButton>
              )}
              <label className={STYLES.toggleArea}>
                <BpkSwitch
                  ariaLabel={toggleLabel}
                  checked={isToggleOn}
                  onChange={onToggleChange}
                  onKeyDown={onToggleKeyDown}
                  variant={SWITCH_VARIANTS.onContrast}
                  small
                />
                <BpkText textStyle={TEXT_STYLES.label1} className={STYLES.toggleLabel}>
                  {toggleLabel}
                </BpkText>
              </label>
            </div>
          </div>
        </div>
      </div>
    </SponsoredHeroBanner>
  );
};

export { ExpandedBanner };
export type { ExpandedBannerProps };
